import { _ as _applyDecoratedDescriptor } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { debug } from '@ember/debug';
import { guidFor } from '@ember/object/internals';
import { sanitizeHtmlString } from 'beebee-helper/helpers/safe-string';
import Quill from 'quill';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{! .bb-editor is the focus-ring hook: the ring has to wrap toolbar and text box\n    together, so it can\'t live on either of them (see custom_new.scss). }}\n<div class=\'bb-editor\' {{did-update this.onValueChange @value}}>\n  <Quill as |Ql|>\n    <Ql.toolbar as |Tb|>\n      <Tb.size @values={{array null \'small\' \'large\' \'huge\'}} />\n      <Tb.group>\n        <Tb.bold />\n        <Tb.italic />\n        <Tb.underline />\n        <Tb.strike />\n      </Tb.group>\n      <Tb.group>\n        <Tb.color />\n        <Tb.background />\n      </Tb.group>\n      <Tb.group>\n        <Tb.align @values={{array null \'center\' \'right\' \'justify\'}} />\n      </Tb.group>\n      <Tb.group>\n        <Tb.list value=\'ordered\' />\n        <Tb.list value=\'bullet\' />\n      </Tb.group>\n      <Tb.group>\n        <Tb.link />\n        <Tb.clean />\n      </Tb.group>\n    </Ql.toolbar>\n\n    <Ql.editor\n      @onInit={{this.onQuillReady}}\n      @onTextChange={{this.adChange}}\n      @name={{this.quillId}}\n      @theme=\'snow\'\n    />\n  </Quill>\n</div>");

var _class;
const {
  StyleAttributor,
  Scope
} = Quill.import('parchment');
const SIZE_MAP = {
  small: '0.75em',
  normal: '1em',
  large: '1.5em',
  huge: '2.5em'
};
class SizeStyleAttributor extends StyleAttributor {
  add(node, value) {
    const cssValue = SIZE_MAP[value] ?? value;
    return super.add(node, cssValue);
  }
  value(node) {
    const cssValue = super.value(node);
    return Object.keys(SIZE_MAP).find(key => SIZE_MAP[key] === cssValue) ?? cssValue;
  }
}
const SizeStyle = new SizeStyleAttributor('size', 'font-size', {
  scope: Scope.INLINE
});
class AlignStyleAttributor extends StyleAttributor {
  add(node, value) {
    if (value === 'left' || value === false) {
      return super.remove(node);
    }
    return super.add(node, value);
  }
}
const AlignStyle = new AlignStyleAttributor('align', 'text-align', {
  scope: Scope.BLOCK
});
Quill.register(SizeStyle, true);
Quill.register(AlignStyle, true);
let BbEditorComponent = (_class = class BbEditorComponent extends Component {
  constructor(...args) {
    super(...args);
    this._quill = null;
    this._lastValue = null;
    this._lastArgsValue = null;
    // preventDefault on dragover is what lets content be DROPPED into the editor;
    // Quill's own clipboard/uploader then handles the drop and emits a normal
    // text-change with source 'user', so nothing extra is needed to persist it.
    //
    // There used to be dragstart/dragend/drop listeners driving an `_isDragging`
    // flag that suppressed external @value reloads mid-drag. That flag was a
    // one-way latch: Quill's own Scroll blot preventDefaults `dragstart` on this
    // very node, and a canceled dragstart means the browser never fires dragend or
    // drop — so once set, the flag stayed set for the component's whole life and
    // silently blocked EVERY later reload (e.g. text inserted by the canned-response
    // picker), which the next keystroke then overwrote with the stale content.
    this._onDragOver = e => {
      e.preventDefault();
    };
  }
  get id() {
    return this.args.id ?? guidFor(this);
  }
  get quillId() {
    return `quill-${this.id}`;
  }
  willDestroy() {
    super.willDestroy(...arguments);

    // LAST-CHANCE FLUSH. Quill batches DOM mutations during IME composition
    // (Composition.handleCompositionStart → scroll.batchStart), so nothing is
    // emitted until compositionend. A teardown while composing — route change,
    // an {{#if}} flipping, a modal closing without blurring the editor first,
    // or Safari not firing compositionend on focus loss — meant the composed
    // text (Chinese/Japanese, macOS dead-key accents like é/à/ò) never reached
    // @setValue at all: quill-editor has already off()'d its handlers by then,
    // and both _handleEvent and adChange drop anything still queued once
    // isDestroying is set. So don't wait for an event: force Quill to observe
    // pending mutations, then persist directly.
    //
    // `update()` and the consumer's setValue can both throw during teardown (a
    // destroyed Quill, or a record that rollbackAttributes has unloaded). A
    // throw here would abort the rest of this destructor, so it is contained.
    if (this._quill) {
      try {
        this._quill.update(Quill.sources.USER);
        this._persist();
      } catch (e) {
        debug(`bb-editor: could not flush the pending edit on teardown — ${e}`);
      }
      this._quill.root.removeEventListener('dragover', this._onDragOver);
    }
    this._quill = null;
    this._lastValue = null;
    this._lastArgsValue = null;
  }

  // Called by @onInit on Ql.editor — quill is fully initialized at this point
  onQuillReady(quill) {
    this._quill = quill;
    this._lastArgsValue = this.args.value;
    this._loadContent(this.args.value);
    quill.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
      delta.ops = delta.ops.map(op => {
        if (op.attributes?.background) {
          delete op.attributes.background;
        }
        return op;
      });
      return delta;
    });
    quill.root.addEventListener('dragover', this._onDragOver);
  }

  // Called by did-update when @value changes after first render
  onValueChange() {
    if (!this._quill) return;
    if (this._lastArgsValue === this.args.value) return;
    this._lastArgsValue = this.args.value;
    this._loadContent(this.args.value);
  }

  // Quill v2 getSemanticHTML() replaces all spaces with &nbsp; (see slab/quill#4509).
  // This breaks word-wrap in templates outside the editor.
  // The regex keeps consecutive &nbsp; for intentional multiple spaces but converts
  // the trailing one back to a regular space so the browser can break the line.
  //
  // Quill v2 also outputs <p></p> for empty lines. An empty <p> collapses to 0 height
  // when rendered outside the editor, so we add <br> to preserve visual spacing.
  _normalizeHTML(html) {
    if (!html) return html;
    return html.replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ').replaceAll('<p></p>', '<p><br></p>');
  }

  // Quill's clipboard.convert() drops <br> elements that are direct children of the
  // root container (i.e. between block-level elements, not inside a <p>).
  // We restructure flat HTML into explicit <p> blocks so Quill preserves spacing:
  //   - 1 <br>  → paragraph split (no blank line)
  //   - 2 <br>  → paragraph split + 1 empty <p><br></p>  (one blank line)
  //   - N <br>  → paragraph split + (N-1) empty paragraphs
  // The old 1:1 replacement of each <br> with <p><br></p> doubled blank lines
  // because <br /><br /> (one blank line) became two empty paragraphs.
  _preprocessHTMLForQuill(html) {
    if (!html) return html;
    const div = document.createElement('div');
    // Sanitize before assigning to a live element: unlike Quill's own
    // DOMParser-based clipboard.convert, innerHTML on a document-created
    // element executes event handlers (e.g. <img src=x onerror=...>) from
    // stored user content. sanitizeHtmlString strips script tags, on*
    // attributes and javascript: URLs.
    div.innerHTML = sanitizeHtmlString(html);
    const childArray = Array.from(div.childNodes);
    if (!childArray.some(n => n.nodeName === 'BR')) return div.innerHTML;

    // Block-level tags must not be nested inside the synthetic <p>: doing so
    // (e.g. <h1>A</h1><br><p>B</p>) yields <p><h1>…</h1></p> which reparses into
    // extra/broken paragraphs. Flush the inline buffer, then append the block
    // node directly at root level.
    const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'TABLE', 'HR']);
    const result = document.createElement('div');
    let currentP = document.createElement('p');
    const flushP = () => {
      if (currentP.childNodes.length > 0) {
        result.appendChild(currentP);
        currentP = document.createElement('p');
      }
    };
    for (const node of childArray) {
      if (node.nodeName === 'BR') {
        if (currentP.childNodes.length === 0) {
          currentP.innerHTML = '<br>';
        }
        result.appendChild(currentP);
        currentP = document.createElement('p');
      } else if (BLOCK_TAGS.has(node.nodeName)) {
        flushP();
        result.appendChild(node.cloneNode(true));
      } else {
        currentP.appendChild(node.cloneNode(true));
      }
    }
    if (currentP.childNodes.length > 0) {
      result.appendChild(currentP);
    }
    return result.innerHTML;
  }

  // Called on the editor's text-change (wired via @onTextChange, which forwards
  // Quill's `source`). Persist ONLY genuine user edits.
  //
  // Programmatic content changes — the initial load and every external @value
  // reload — go through setContents(…, 'silent'). Those must NEVER be written back:
  // getSemanticHTML() is momentarily unstable right after setContents (Quill applies
  // the delta across runloop ticks), so the `_lastValue` equality guard alone could
  // still emit a stale/empty string and blank the field on the next save. With every
  // editor on a card now mounting up front, that fired for fields the user never
  // touched and wiped their stored content. Gating on source='user' closes the whole
  // class: a load can never masquerade as an edit.
  //
  // `source` is undefined only for the teardown flush below, which is a real edit.
  adChange(_delta, _oldDelta, source) {
    if (this.isDestroying || this.isDestroyed) return;
    this._persist(source);
  }

  // Shared by adChange and the teardown flush.
  _persist(source) {
    if (!this._quill?.root) return;
    if (source && source !== Quill.sources.USER) return;
    const value = this._normalizeHTML(this._quill.getSemanticHTML());

    // Never write an empty string. Quill's Editor#getHTML returns '' when the
    // document is momentarily line-less (it happens inside setContents, between
    // deleteText and applyDelta), and '' is not a state the user can produce:
    // an emptied editor yields '<p><br></p>'. Without this, one unlucky read
    // labeled 'user' would blank the stored field.
    if (!value) return;
    if (value === this._lastValue) return;

    // `?.` only guards null/undefined. A consumer that passes `@setValue={{mut x}}`
    // hands us a mutable CELL, which is truthy but not callable, so the optional
    // call still threw "setValue is not a function" — and because adChange runs
    // inside a backburner autorun, that surfaced as an unhandled rejection with
    // the user's edit silently dropped. Reject anything non-callable here and say
    // so in development; the correct binding is `{{fn (mut x)}}`.
    if (typeof this.args.setValue !== 'function') {
      if (this.args.setValue != null) {
        debug('<BbEditor> received a non-callable @setValue; use {{fn (mut …)}}, not {{mut …}}');
      }
      return;
    }

    // Baselines advance ONLY after the write actually happened. Setting
    // `_lastValue` first meant a write that then bailed (non-callable @setValue)
    // or threw (`{{fn (mut …)}}` onto a record rollbackAttributes had unloaded —
    // ED 5.8 throws, and the backburner autorun swallows it) left the baseline
    // claiming the text was saved: nothing was sent again until the user typed
    // something that produced a *different* string. Text typed and then left
    // alone was gone with no signal.
    this.args.setValue(value);
    this._lastValue = value;
    // The consumer writes @value from this same string, so record it as the
    // incoming baseline too: otherwise every keystroke re-entered _loadContent
    // through did-update on the hot typing path.
    this._lastArgsValue = value;
  }

  // Push content into the editor. Always a programmatic write (SILENT source), so
  // adChange's source gate already drops the resulting text-change. We still record
  // the loaded content as `_lastValue` — not as a "load echo" filter, but as the
  // change-detector baseline: it is the editor's current known value, so a later
  // adChange only writes when the content actually differs. This also covers the one
  // way a load could still reach adChange — Quill's MutationObserver can relabel a
  // post-load DOM normalization as source='user' — by making that spurious event a
  // no-op (same value) instead of a redundant write.
  _loadContent(html) {
    if (!this._quill) return;
    if (!html) {
      this._quill.setText('', Quill.sources.SILENT);
      this._lastValue = this._normalizeHTML(this._quill.getSemanticHTML());
      return;
    }
    const currentHTML = this._normalizeHTML(this._quill.getSemanticHTML());
    if (currentHTML === html) {
      this._lastValue = currentHTML;
      return;
    }
    const delta = this._quill.clipboard.convert({
      html: this._preprocessHTMLForQuill(html)
    });
    this._quill.setContents(delta, Quill.sources.SILENT);
    this._lastValue = this._normalizeHTML(this._quill.getSemanticHTML());
  }
}, _applyDecoratedDescriptor(_class.prototype, "onQuillReady", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onQuillReady"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onValueChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onValueChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "adChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "adChange"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbEditorComponent);

export { BbEditorComponent as default };
//# sourceMappingURL=bb-editor.js.map
